<template>
  <div
    class="iq-sidebar-outer"
    :class="{ 'iq-sidebar-outer--collapsed': isCollapsed }"
    :style="sidebarOuterStyle"
  >
    <div
      class="iq-sidebar"
      :class="{ 'iq-sidebar--collapsed': isCollapsed, 'iq-sidebar--embedded': props.embedded }"
    >
      <!-- Top Icons Section -->
      <div class="iq-top-icons">
        <button class="iq-top-icon" aria-label="Expand or collapse sidebar" @click="handleToggleClick">
          <v-img :src="require('@components/assets/iq-toggle-icons.svg')" />
        </button>
        <button class="iq-top-icon" aria-label="New chat" type="button" @click="handleEditClick">
          <v-img :src="require('@components/assets/iq-edit-icons.svg')" />
        </button>
      </div>

      <!-- Collapsed rail: branding + conversation initials (tooltips show full title) -->
      <div v-if="isCollapsed" class="iq-collapsed-rail">
        <div class="iq-collapsed-brand" aria-hidden="true">
          <span class="iq-collapsed-brand-mark">IQ</span>
        </div>
        <div v-if="hasConversations" class="iq-collapsed-chats">
          <v-tooltip
            v-for="s in allSuggestionsFlat"
            :key="s.id"
            bottom
            content-class="iq-collapsed-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="iq-collapsed-chat-btn"
                :class="{ 'iq-collapsed-chat-btn--active': s.id === activeItemId }"
                :aria-label="s.title || 'Conversation'"
                v-bind="attrs"
                v-on="on"
                @click="selectSuggestion(s.id)"
              >
                {{ collapsedInitial(s.title) }}
              </button>
            </template>
            <span>{{ s.title || "Conversation" }}</span>
          </v-tooltip>
        </div>
        <div v-else class="iq-collapsed-empty" aria-hidden="true">—</div>
      </div>

      <!-- Header Section -->
      <div v-if="!isCollapsed" class="iq-header">
        <div class="iq-header-left">
          <div class="iq-logo-container">
            <div class="iq-logo-square">
              <IQButton size="large" :countNewMsg="0" />
            </div>
          </div>
          <span class="iq-header-title" :class="{ 'iq-header-title--muted': !hasConversations }">{{
            headerLabel
          }}</span>
        </div>
      </div>

      <!-- Content Section -->
      <div v-if="!isCollapsed" class="iq-content">
        <!-- Today Section -->
        <div class="iq-section" v-if="props.todaySuggestions.length > 0">
          <h3 class="iq-section-title">Today</h3>
          <div class="iq-suggestions-list">
            <div
              v-for="suggestion in props.todaySuggestions"
              :key="suggestion.id"
              class="iq-suggestion-item"
              :class="{ 'iq-suggestion-item--active': suggestion.id === activeItemId }"
              @click="selectSuggestion(suggestion.id)"
            >
              <span class="iq-suggestion-text">{{ suggestion.title }}</span>
              <!-- <span 
                v-if="suggestion.id === activeItemId" 
                class="iq-suggestion-menu" 
                @click.stop="openMenu(suggestion)"
              >
                <v-img :src="require('@components/assets/sidebarMenu.svg')" />
              </span> -->
            </div>
          </div>
        </div>

        <!-- Previous 7 Days Section -->
        <div class="iq-section" v-if="props.previous7DaysSuggestions.length > 0">
          <h3 class="iq-section-title">Previous 7 Days</h3>
          <div class="iq-suggestions-list">
            <div
              v-for="suggestion in props.previous7DaysSuggestions"
              :key="suggestion.id"
              class="iq-suggestion-item"
              :class="{ 'iq-suggestion-item--active': suggestion.id === activeItemId }"
              @click="selectSuggestion(suggestion.id)"
            >
              <span class="iq-suggestion-text">{{ suggestion.title }}</span>
              <!-- <span 
                v-if="suggestion.id === activeItemId" 
                class="iq-suggestion-menu" 
                @click.stop="openMenu(suggestion)"
              >
                <v-img :src="require('@components/assets/sidebarMenu.svg')" />
              </span> -->
            </div>
          </div>
        </div>

        <!-- Previous 30 Days Section -->
        <div class="iq-section" v-if="props.previous30DaysSuggestions.length > 0">
          <h3 class="iq-section-title">Previous 30 Days</h3>
          <div class="iq-suggestions-list">
            <div
              v-for="suggestion in props.previous30DaysSuggestions"
              :key="suggestion.id"
              class="iq-suggestion-item"
              :class="{ 'iq-suggestion-item--active': suggestion.id === activeItemId }"
              @click="selectSuggestion(suggestion.id)"
            >
              <span class="iq-suggestion-text">{{ suggestion.title }}</span>
              <!-- <span 
                v-if="suggestion.id === activeItemId" 
                class="iq-suggestion-menu" 
                @click.stop="openMenu(suggestion)"
              >
                <v-img :src="require('@components/assets/sidebarMenu.svg')" />
              </span> -->
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import IQButton from "@components/IQButton/IQButton.vue";

const props = defineProps({
  /** When true, fills parent height (e.g. IQ overlay) instead of the viewport. */
  embedded: {
    type: Boolean,
    default: false,
  },
  todaySuggestions: {
    type: Array,
    default: () => [],
  },
  previous7DaysSuggestions: {
    type: Array,
    default: () => [],
  },
  previous30DaysSuggestions: {
    type: Array,
    default: () => [],
  },
  emptyMessage: {
    type: String,
    default: "No conversations found",
  },
  /** Parent-controlled highlight (IQ overlay thread selection). */
  selectedChatId: {
    type: String,
    default: null,
  },
  controlledSelection: {
    type: Boolean,
    default: false,
  },
});

const hasConversations = computed(
  () =>
    props.todaySuggestions.length > 0 ||
    props.previous7DaysSuggestions.length > 0 ||
    props.previous30DaysSuggestions.length > 0
);

/** Single list for collapsed rail (order: Today → 7 days → 30 days). */
const allSuggestionsFlat = computed(() => [
  ...props.todaySuggestions,
  ...props.previous7DaysSuggestions,
  ...props.previous30DaysSuggestions,
]);

const collapsedInitial = (title) => {
  const s = (title || "").trim();
  if (!s) return "?";
  const match = s.match(/[a-zA-Z0-9]/);
  return match ? match[0].toUpperCase() : "?";
};

const headerLabel = computed(() =>
  hasConversations.value ? "Suggestions/previous tabs" : props.emptyMessage
);

const sidebarOuterStyle = computed(() => ({
  width: isCollapsed.value ? "50px" : "216px",
  transition: "width 0.2s ease",
  ...(props.embedded ? { height: "100%", minHeight: 0 } : {}),
}));

const emit = defineEmits(["suggestion-selected", "toggle-click", "edit-click", "menu-click"]);

const selectedSuggestion = ref(null);
const isCollapsed = ref(false);

const activeItemId = computed(() => {
  if (props.controlledSelection && props.selectedChatId != null && props.selectedChatId !== "") {
    return props.selectedChatId;
  }
  return selectedSuggestion.value;
});

watch(
  () => props.selectedChatId,
  (id) => {
    if (!props.controlledSelection) return;
    selectedSuggestion.value = id;
  },
  { immediate: true }
);

const syncSelectionFromProps = () => {
  if (props.controlledSelection) {
    return;
  }
  const allSuggestions = [
    ...props.todaySuggestions,
    ...props.previous7DaysSuggestions,
    ...props.previous30DaysSuggestions,
  ];
  const activeSuggestion = allSuggestions.find((s) => s.active);
  if (activeSuggestion) {
    selectedSuggestion.value = activeSuggestion.id;
    return;
  }
  if (selectedSuggestion.value && allSuggestions.some((s) => s.id === selectedSuggestion.value)) {
    return;
  }
  selectedSuggestion.value = allSuggestions[0]?.id ?? null;
};

watch(
  () => [props.todaySuggestions, props.previous7DaysSuggestions, props.previous30DaysSuggestions],
  () => syncSelectionFromProps(),
  { deep: true, immediate: true }
);

const selectSuggestion = (suggestionId) => {
  selectedSuggestion.value = suggestionId;
  emit("suggestion-selected", suggestionId);
};

const handleToggleClick = () => {
  isCollapsed.value = !isCollapsed.value;
  emit("toggle-click");
};

const handleEditClick = () => {
  emit("edit-click");
};

const openMenu = (suggestion) => {
  emit("menu-click");
};
</script>

<style scoped>
.iq-sidebar {
  background: #ffffff;
  color: #101828;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #e5e7eb;
}

.iq-sidebar--embedded {
  height: 100%;
  min-height: 0;
}

/* Top Icons */
.iq-top-icons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  padding: 8px 8px;
  flex-shrink: 0;
}

.iq-sidebar-outer--collapsed .iq-top-icons {
  flex-direction: column;
  gap: 4px;
  padding: 8px 6px;
}

.iq-top-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.iq-top-icon:hover {
  background: #f2f4f7;
}

/* Collapsed rail */
.iq-collapsed-rail {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  padding: 4px 0 12px;
  gap: 10px;
}

.iq-collapsed-brand {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 6px 0;
  background: #f9fafb;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
}

.iq-collapsed-brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: #ed2738;
  border-radius: 4px;
  font-family: Helvetica, sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  letter-spacing: -0.02em;
}

.iq-collapsed-chats {
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
}

.iq-collapsed-chats::-webkit-scrollbar {
  width: 4px;
}
.iq-collapsed-chats::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 2px;
}

.iq-collapsed-chat-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: #f2f4f7;
  color: #344054;
  font-family: Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.iq-collapsed-chat-btn:hover {
  background: #eaecf0;
}

.iq-collapsed-chat-btn--active {
  background: #ffffff;
  box-shadow: 0 0 0 2px #ed2738;
}

.iq-collapsed-empty {
  font-size: 14px;
  color: #98a2b3;
  padding-top: 8px;
}

/* Header */
.iq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  color: #101828;
  padding: 12px 16px;
  border-bottom: 1px solid #eaecf0;
}

.iq-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.iq-logo-container {
  display: flex;
  align-items: center;
}

.iq-logo-square {
  width: 24px;
  height: 24px;
  background: #ed2738;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.iq-logo-text {
  color: #ffffff;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
}

.iq-header-title {
  font-family: Helvetica;
  font-weight: 400;
  font-style: Regular;
  font-size: 11px;
  line-height: 100%;
  user-select: none;
  color: #101828;
}

.iq-header-title--muted {
  color: #98a2b3;
  line-height: 130%;
}

/* Content */
.iq-content {
  flex: 1;
  padding: 16px 8px 16px 16px;
  overflow-y: auto;
}

.iq-section {
  margin-bottom: 24px;
}

.iq-section-title {
  font-family: Helvetica;
  font-size: 10px;
  color: #98a2b3;
  margin: 0 0 8px 0;
  letter-spacing: 0.05em;
  margin-left: -3px;
}

.iq-suggestions-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: -6px;
}

.iq-suggestion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Helvetica;
  font-size: 11px;
  padding: 8px 4px 7px 4px;
  border-radius: 4px;
  cursor: pointer;
  color: #344054;
  transition: background-color 0.2s ease;
}

.iq-suggestion-item:hover {
  background: #f2f4f7;
}

.iq-suggestion-item--active {
  background: #f2f4f7;
  color: #101828;
}

.iq-suggestion-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 6px;
}

.iq-suggestion-menu {
  color: #98a2b3;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  position: relative;
  top: 0px;
  left: -2px;
  letter-spacing: 1px;
}

.iq-suggestion-menu:hover {
  color: #344054;
}

/* Scrollbar */
.iq-content::-webkit-scrollbar {
  width: 6px;
}
.iq-content::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 3px;
}
</style>

<style>
/* Unscoped: Vuetify portaled tooltip */
.iq-collapsed-tooltip.v-tooltip__content {
  font-size: 12px !important;
  max-width: 240px;
  padding: 8px 10px !important;
}
</style>
